import Component from '@glimmer/component';
import { action, setProperties, get } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask, restartableTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { DESK_ICON, DISABLED_DESK_ICON, RED_PIN_ICON, SCHEDULED_DESK_ICON } from 'garaje/utils/map/map-icons';

/**
 * MoveRequestMap Component
 *
 * Displays a floor map for selecting a desk for a move request.
 * Shows all desks with appropriate icons based on availability:
 * - Available (green): no assignment, not shared → clickable
 * - Shared/scheduled (gray + clock): on a schedule → not clickable
 * - Unavailable (gray): assigned or disabled → not clickable
 * - Selected (red pin): the user's chosen desk
 *
 * @param {Array<MapFloor>} floors          Available floors
 * @param {string|null}     currentFloorId  Pre-selected floor ID
 * @param {Desk|null}       selectedDesk    Currently selected desk
 * @param {Function}        onDeskSelect    Callback: (desk|null) => void
 */
export default class MoveRequestMapComponent extends Component {
  @service state;
  @service store;

  @tracked isMapReady = false;
  @tracked mapHeight;
  @tracked mapWidth;
  @tracked currentFloor;
  @tracked desks = [];

  get floorsWithMap() {
    return (this.args.floors ?? []).filter((floor) => floor.rasterImageUrl);
  }

  /**
   * Get desks on the current floor that are placed on the map.
   */
  get placedDesks() {
    if (!this.currentFloor) {
      return [];
    }
    return this.desks.filter((desk) => get(desk, 'placed') && !get(desk, 'isDeleted') && get(desk, 'enabled'));
  }

  /**
   * Determine if a desk is on an assignment to someone else.
   */
  @action
  isDeskScheduled(desk) {
    const assignmentDetails = get(desk, 'assignmentDetails');
    const assignmentIds = desk.hasMany('assignments').ids();
    return (assignmentDetails?.length ?? 0) > 0 || assignmentIds.length > 0;
  }

  /**
   * Determine if a desk is available for move request selection.
   * Available = enabled + not assigned to someone + not a shared desk
   */
  @action
  isDeskAvailable(desk) {
    if (!get(desk, 'enabled')) return false;
    if (get(desk, 'assignedTo')) return false;
    if (this.isDeskScheduled(desk)) return false;
    return true;
  }

  /**
   * Apply icons to desks based on their status and selection state.
   */
  get placedDesksWithIcons() {
    const selectedDeskId = this.args.selectedDesk ? get(this.args.selectedDesk, 'id') : null;

    return this.placedDesks.map((desk) => {
      const deskId = get(desk, 'id');
      const isSelected = deskId === selectedDeskId;

      if (isSelected) {
        this.setDeskIcon(desk, RED_PIN_ICON);
      } else if (this.isDeskScheduled(desk)) {
        this.setDeskIcon(desk, SCHEDULED_DESK_ICON);
      } else if (this.isDeskAvailable(desk)) {
        this.setDeskIcon(desk, DESK_ICON);
      } else {
        this.setDeskIcon(desk, DISABLED_DESK_ICON);
      }

      return desk;
    });
  }

  setDeskIcon(desk, icon) {
    setProperties(desk, {
      iconClassname: icon.iconClassname,
      iconSize: icon.iconSize,
      iconHtml: '',
    });
  }

  @dropTask
  *loadFloorsTask() {
    // yield to satisfy generator requirement
    yield Promise.resolve();
    if (this.floorsWithMap.length > 0) {
      // Pre-select floor matching the current live map floor, or the first available
      const matchingFloor = this.args.currentFloorId
        ? this.floorsWithMap.find((f) => f.id === this.args.currentFloorId)
        : null;
      this.currentFloor = matchingFloor ?? this.floorsWithMap[0] ?? null;
    } else {
      this.currentFloor = null;
    }
  }

  @restartableTask
  *loadDesksTask() {
    if (!this.currentFloor) {
      return;
    }

    const params = {
      filter: {
        'location-id': get(this.state.currentLocation, 'id'),
      },
      include: 'amenities',
    };

    const desks = yield this.store.query('desk', params);
    this.desks = desks.filter((desk) => get(desk, 'floor.id') === this.currentFloor?.id && get(desk, 'enabled'));
  }

  @dropTask
  *onModalLoad() {
    yield this.loadFloorsTask.perform();
    yield this.loadDesksTask.perform();
    yield this.loadMapImageTask.perform();
  }

  @dropTask
  *loadMapImageTask() {
    if (!this.currentFloor) {
      return;
    }

    yield new Promise((resolve, reject) => {
      const image = new Image();
      image.src = this.currentFloor.rasterImageUrl;
      image.onerror = (err) => {
        // eslint-disable-next-line no-console
        console.error('error loading map', err);
        reject();
      };
      image.onload = (ev) => {
        if (!this.isDestroyed && !this.isDestroying) {
          const { height: mapHeight, width: mapWidth } = ev.target;
          setProperties(this, {
            mapHeight,
            mapWidth,
          });
        }
        resolve();
      };
    });
  }

  @dropTask
  *onFloorChange(floor) {
    this.currentFloor = floor;
    // Notify parent so it can persist the floor selection across phase changes
    if (this.args.onFloorChange) {
      this.args.onFloorChange(floor.id);
    }
    // Clear selected desk when switching floors
    if (this.args.onDeskSelect) {
      this.args.onDeskSelect(null);
    }
    yield this.loadDesksTask.perform();
    yield this.loadMapImageTask.perform();
  }

  @action
  toggleIsMapReady() {
    this.isMapReady = true;
  }

  @action
  handleDeskClick(desk) {
    if (!this.isDeskAvailable(desk)) {
      return;
    }

    const selectedDeskId = this.args.selectedDesk ? get(this.args.selectedDesk, 'id') : null;
    const clickedDeskId = get(desk, 'id');

    if (clickedDeskId === selectedDeskId) {
      // Deselect
      this.args.onDeskSelect(null);
    } else {
      // Select
      this.args.onDeskSelect(desk);
    }
  }
}
